import caseColumnDefs from "@/components/Table/MrtCaseColumnLists/CaseColumnDefs.tsx";
import AttorneyLitigationFilters from "@/components/Table/MrtCaseFilters/AttorneyLitigationFilters.tsx";
import AttorneyPreLitFilters from "@/components/Table/MrtCaseFilters/AttorneyPreLitFilters.tsx";
import CaseManagerFilters from "@/components/Table/MrtCaseFilters/CaseManagerFilters.tsx";
import DisbursementSpecialistFilters from "@/components/Table/MrtCaseFilters/DisbursementSpecialistFilters.tsx";
import IntakeSpecialistFilters from "@/components/Table/MrtCaseFilters/IntakeSpecialistFilters.tsx";
import MedicalRecordsClerkFiltersNoTeam from "@/components/Table/MrtCaseFilters/MedicalRecordsClerkFiltersNoTeam.tsx";
import SearchableToolbar, { SEARCH_FIELDS } from "@/components/Table/SearchableToolbar.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { getSolReportMrtStyle } from "@/functions/getSolReportMrtStyle.ts";
import { useDeleteCase } from "@/mutations/useDeleteCase.tsx";
import {
    type MrtDashboardCase,
    useFetchMrtDashboardCases,
} from "@/queries/useFetchMrtDashboardCases.ts";
import { getErrorMessage } from "@/utils/api.ts";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton } from "@mui/material";
import {
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_SortingState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
    taskDashboard: boolean;
};

const MrtCaseTableDashboard = ({ taskDashboard }: Props) => {
    const { hasPermission } = useEmployee();
    const { employee } = useEmployee();
    const navigate = useNavigate();
    const deleteCase = useDeleteCase();
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [selectedTab, setSelectedTab] = useState("");

    const [selectedSearchFields, setSelectedSearchFields] = useState<string[]>(
        SEARCH_FIELDS.filter((field) => field.default).map((field) => field.fieldName),
    );

    const { data, isError, isRefetching, isLoading, refetch, error } = useFetchMrtDashboardCases({
        selectedTab,
        taskDashboard,
        columnFilters,
        globalFilter,
        activeFilter: null,
        pagination,
        sorting,
        searchFields: selectedSearchFields,
    });

    if (isError) {
        console.error("error fetching cashboard cases", error);
    }

    const handleRowClick = (row: MRT_Row<MrtDashboardCase>) => {
        navigate(`/case-detail/${row.original.id}`);
    };

    const handleDeleteRow = (row: MRT_Row<MrtDashboardCase>) => {
        if (window.confirm("Are you sure you want to delete this Case?")) {
            deleteCase.mutate(
                {
                    caseUUID: row.original.idUUID,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Case removed.", { variant: "success" });
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Case could not be removed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        }
    };

    const columns = useMemo<MRT_ColumnDef<MrtDashboardCase>[]>(() => {
        const columnDefs: MRT_ColumnDef<MrtDashboardCase>[] = [...caseColumnDefs];

        if (
            hasPermission("delete:case") &&
            !columnDefs.some((column) => column.accessorKey === "delete")
        ) {
            columnDefs.push({
                accessorKey: "delete",
                header: "Delete",
                size: 50,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ row }) => (
                    <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            handleDeleteRow(row);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                ),
            });
        }

        return columnDefs;
    }, [handleDeleteRow, hasPermission, employee?.dashboardVersion]);

    useEffect(() => {
        void refetch();
    }, [globalFilter, selectedTab, refetch, taskDashboard]);

    const table = useMaterialReactTable({
        columns,
        data: isLoading || isRefetching || isError || data === undefined ? [] : data.data,
        enableColumnActions: false,
        defaultColumn: {
            size: 30,
            muiTableBodyCellProps: {
                sx: {
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                },
            },
        },
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: true,
            density: "compact",
        },

        // top toolbar
        enableTopToolbar: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,

        muiTopToolbarProps: {
            sx: { display: "flex", justifyContent: "space-between", alignItems: "center" },
        },

        // Add custom SearchableToolbar component
        renderTopToolbarCustomActions: () => (
            <SearchableToolbar
                searchFields={SEARCH_FIELDS}
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
                selectedSearchFields={selectedSearchFields}
                onSelectedSearchFieldsChange={setSelectedSearchFields}
                placeholder="Search cases..."
            />
        ),

        // server side searching and pagination
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,

        // state
        rowCount: data?.meta.totalRowCount ?? 0,
        state: {
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading || isRefetching,
            sorting,
        },

        // design
        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading data",
              }
            : undefined,
        muiTableBodyRowProps: ({ row }: { row: MRT_Row<MrtDashboardCase> }) => {
            return {
                onClick: () => handleRowClick(row),
                sx: {
                    cursor: "pointer",
                    "& .MuiTableCell-root": {
                        ...getSolReportMrtStyle({
                            caseStatus: row.original.caseStatus ?? "",
                            dateOfBirth: row.original.idPeople?.dateofbirth ?? null,
                        }),
                    },
                    "& .MuiTableSortLabel-icon": {
                        display: "none",
                    },
                },
            };
        },
    });

    return (
        <Box>
            {employee?.dashboardVersion === "Attorney" && employee?.attorneyType === "Pre-Lit" && (
                <AttorneyPreLitFilters selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
            )}

            {employee?.dashboardVersion === "Attorney" &&
                employee?.attorneyType === "Litigation" && (
                    <AttorneyLitigationFilters
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                    />
                )}

            {employee?.dashboardVersion === "CaseManager" && (
                <CaseManagerFilters selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
            )}

            {employee?.dashboardVersion === "DisbursementSpecialist" && (
                <DisbursementSpecialistFilters
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                />
            )}

            {employee?.dashboardVersion === "IntakeSpecialist" && (
                <IntakeSpecialistFilters
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                />
            )}

            {employee?.dashboardVersion === "MedicalRecordsClerk" && employee.team && (
                <CaseManagerFilters selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
            )}

            {employee?.dashboardVersion === "MedicalRecordsClerk" && !employee.team && (
                <MedicalRecordsClerkFiltersNoTeam
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                />
            )}

            <MaterialReactTable table={table} />
        </Box>
    );
};

export default MrtCaseTableDashboard;
